import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  Injectable,
  HostListener,
  ViewChild,
} from '@angular/core';
import { Config } from '@core/config';
import { CardList } from '@shared/models/card-list';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  MatPaginator,
  MatPaginatorIntl,
  PageEvent,
} from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { FilterCountService } from '@shared/services/filter_count.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getActualImage,
  getCategoryName,
  onImageError,
} from '@app/@shared/tools/tools';
import { GAService } from '@app/@shared/services/google-analytics.service';
import { FormsService } from '@shared/services/forms.service';
import { ResponseData } from '@app/@shared/models/temp-form';

@Injectable()
export class PaginatorIntl implements MatPaginatorIntl {
  changes = new Subject<void>();
  public firstPageLabel: string;
  public itemsPerPageLabel: string;
  public lastPageLabel: string;
  public previousPageLabel: string;
  public nextPageLabel: string;

  constructor(private translateService: TranslateService) {
    this.firstPageLabel = translateService.instant(`FirstPage`);
    this.itemsPerPageLabel = translateService.instant(`ItemsPerPages`);
    this.lastPageLabel = translateService.instant(`LastPage`);
    this.nextPageLabel = translateService.instant(`NextPage`);
    this.previousPageLabel = translateService.instant(`PreviousPage`);
  }

  /**
   * @param page
   * @param pageSize
   * @param length
   * @returns
   */
  public getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0) {
      return this.translateService.instant(`PageNumber`, {
        current: 1,
        total: 1,
      });
    }
    const amountPages = Math.ceil(length / pageSize);
    return this.translateService.instant(`PageNumber`, {
      current: page + 1,
      total: amountPages,
    });
  }
}

@Component({
  selector: 'marker-list',
  templateUrl: './map-marker-list.component.html',
  styleUrls: ['./map-marker-list.component.scss'],
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
})
export class MapMarkerListComponent implements OnInit {
  @Input() set catId(input: string) {
    this._catId = input;
    this.filterDisabled = this._catId === '5';
  }

  get catId(): string {
    return this._catId;
  }

  @Input() set miniCard(input: CardList[]) {
    if (input != null) {
      this._miniCard = input;
      this.nbResults = input.length;
      this.pageContent = this._miniCard.slice(
        this.page * this.currentPageSize,
        this.page * this.currentPageSize + this.currentPageSize
      );
      if (this.nbResults === 0 && this.formService.fiches.length > 0) {
        // Need this fallback to ensure we get data to display when on FO responsive
        this.nbResults = this.formService.fiches.length;
        this.pageContent = this.formService.fiches.slice(
          this.page * this.currentPageSize,
          this.page * this.currentPageSize + this.currentPageSize
        );
      }
      this.updateCurrentPageIndex();
    }
  }

  get miniCard(): CardList[] {
    return this._miniCard;
  }
  @ViewChild(CdkVirtualScrollViewport) scroll: CdkVirtualScrollViewport;
  @ViewChild('paginator') paginator?: MatPaginator;
  @Output() collapsed = new EventEmitter<boolean>();
  @Output() hovered = new EventEmitter<CardList | boolean>();
  @Input() filterPanel: BehaviorSubject<boolean>;
  @Input() zoom: BehaviorSubject<number>;

  @Input() isCollapsed: boolean;
  @Input() title: string[];
  // Paginator component logic access.
  nbResults: number;
  icons = Config.iconSubCat;
  col: number;
  isMobile: boolean;
  pageSize: number[];
  page: number;
  pageContent: CardList[];
  currentPageSize: number;
  filters: string[] = [];
  filterDisabled: boolean;
  filterCount: number;
  currentScreenWidth: number;
  cat = [
    { id: 0, name: 'heritage' },
    { id: 1, name: 'circuits' },
    { id: 2, name: 'services' },
    { id: 3, name: 'events' },
    { id: 4, name: 'quizz' },
  ];
  inspirationTags: ResponseData[] = [];

  private _miniCard: CardList[];
  private _catId: string;
  private subscriptions: Subscription[];

  constructor(
    private filtersCount: FilterCountService,
    private viewport: ViewportScroller,
    private router: Router,
    private route: ActivatedRoute,
    public gaService: GAService,
    public formService: FormsService
  ) {
    this.filterDisabled = false;
    this.isMobile = false;
    this.col = 2;
    this.pageSize = [10, 20, 50];
    this.currentPageSize = 10;
    this.page = 0;
    this.isMobile =
      /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    this.subscriptions = [];
  }

  async editInspirationTags() {
    await this.router.navigate(['suggestions']);
  }

  async removeInspirationTage(index: number) {
    this.inspirationTags[index] = undefined;
    this.formService.inspirationTags = JSON.parse(
      JSON.stringify(this.inspirationTags)
    );
    await this.formService.getFormResults();
    await this.router.navigate(['carte/5']);
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.filtersCount.sharedValue$.subscribe(
        (data) => (this.filterCount = data)
      )
    );
    this.onResize();
    if (this.catId === '5') {
      if (this.formService.inspirationTags.length > 0)
        this.inspirationTags = JSON.parse(
          JSON.stringify(this.formService.inspirationTags)
        );
      if (this.inspirationTags.length === 0)
        await this.router.navigate(['suggestions']);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Set the currentScreenWidth attribute with actual screen width device.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.currentScreenWidth = this.isMobile
      ? window.screen.width
      : window.innerWidth;
  }

  identify(index: number, item: CardList): string {
    return item.id;
  }

  reduce(): void {
    this.collapsed.emit(!this.isCollapsed);
  }

  activateFilter(): void {
    // Can't set the same value twice in a raw this way
    // Need to cheat a bit
    this.zoom.next(this.zoom.value == 10 ? 10.0001 : 10);
    this.filterPanel.next(false);
  }

  swapPage(event: PageEvent): void {
    this.currentPageSize = event.pageSize;
    this.page = event.pageIndex;
    this.pageContent = this.miniCard.slice(
      event.pageIndex * event.pageSize,
      event.pageIndex * event.pageSize + event.pageSize
    );
    this.scroll.scrollToOffset(0);
    this.viewport.scrollToPosition([0, 0]);
  }

  onOver(card: CardList) {
    this.hovered.emit(card);
  }

  closeOnOver() {
    this.hovered.emit(false);
  }

  public checkIfDateIsInterval(dateStart: string, dateEnd?: string): boolean {
    // Returns true if start date and end date are different

    const start = new Date(dateStart);
    const end = new Date(dateEnd);
    return (
      start.getDate() != end.getDate() ||
      start.getMonth() != end.getMonth() ||
      start.getFullYear() != end.getFullYear()
    );
  }

  public checkIfDateIsCurrent(dateStart: string, dateEnd?: string): boolean {
    // Returns true if current day date == start date, or current day comprised between start date and end date

    const current = new Date();
    const start = new Date(dateStart);
    if (dateEnd === undefined) {
      return (
        start.getDate() == current.getDate() &&
        start.getMonth() == current.getMonth() &&
        start.getFullYear() == current.getFullYear()
      );
    } else {
      const end = new Date(dateEnd);
      return start <= current && current <= end;
    }
  }

  public checkIfDateIsCurrentOrFuture(
    dateStart: string,
    dateEnd?: string
  ): boolean {
    // Returns true if current day date <= start date, or if current day date is anywhere before end date

    const current = new Date();
    const start = new Date(dateStart);
    if (dateEnd === undefined) {
      return start >= current;
    } else {
      const end = new Date(dateEnd);
      return end >= current;
    }
  }

  formatDate(
    dateIn: string,
    format: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
  ): string {
    const date = new Date(dateIn);
    return `${date.getDate()} ${date.toLocaleString('fr-FR', {
      month: format,
    })}`;
  }

  setUrl() {
    window.sessionStorage.setItem('lastMap', this.router.url);
  }

  toInt(arg: string): number {
    return parseInt(arg);
  }

  /**
   * Get the current schedule given the duration from param.
   * @param {number} duration
   * @returns {string} stringify duration.
   */
  public getDurationStringified(duration: number): string {
    let minutes = duration % 1;
    const hours = duration - minutes;
    minutes = Math.round(minutes * 60);
    return `${hours}h${minutes < 10 ? '0' : ''}${minutes}`;
  }

  /**
   * Get the correct image given the param.
   * When no data is founded, then we it's a placeholder that is returned.
   * @param {CardList} mc
   * @returns {string} url string.
   */
  public getCurrentImage(mc: CardList): string {
    return getActualImage(parseInt(mc.category), mc?.image?.url);
  }

  /**
   * We use this to manage error callback from image call.
   * @param {CardList} mc
   */
  public manageImageError(mc: CardList, img: HTMLImageElement): void {
    if (mc.idSubcategory != null) {
      onImageError(parseInt(mc.category), img, mc.idSubcategory.toString());
    } else {
      onImageError(parseInt(mc.category), img);
    }
  }

  /**
   * Get the current icon.
   * @param {CardList} mc
   * @returns
   */
  public getCurrentIcon(mc: CardList): string {
    if (mc.category == '1') {
      const icon = mc.icon !== undefined ? mc.icon.url.split('poi_') : '';
      const ref =
        icon !== '' ? icon[icon.length - 1].split('.')[0] : 'circuit_1';
      return `icon_${ref}`;
    }
    return `icon_${
      mc.idSubcategory !== null ? this.icons[mc.idSubcategory] : mc.category
    }`;
  }

  public async goToFicheDetail(card: CardList) {
    if (this.inspirationTags)
      this.formService.inspirationTags = this.inspirationTags;
    this.gaService.sendEvent(getCategoryName(card), 'Catégorie');
    let secondParam =
      card.idSubcategory != null ? card.idSubcategory : `${card.category}`;
    if (card.idSubcategory == 1 && card.category == '0') {
      secondParam = 0;
    }

    await this.router.navigate(['fiche', secondParam, card.id]);
  }

  /**
   * Private function used by miniCard function when data from list is updated by the current map.
   * When the max page is lower than the current page, we redirect to the first page.
   */
  private updateCurrentPageIndex(): void {
    if (this.paginator?.pageIndex == null) return;

    const maxPages = Math.round(this.nbResults / this.currentPageSize);
    if (this.paginator.pageIndex > maxPages) {
      this.paginator.firstPage();
    }
  }

  async getNewSuggestions() {
    await this.formService.getFormResults();
    await this.router.navigate(['carte/5']);
  }

  /**
   * We use this to send GA Analytics if user clicked from suggestion results.
   */
  public sendGaEventIfSuggestion(catId: string, card: any) {
    if (catId === '5') {
      this.gaService.sendEvent(
        'Suggestions',
        'Fiche',
        `Fiche ${card.innerID ?? card.old_id}`
      );
    }
  }
}
